import { Button } from '@/components/ui/button';
import { cn } from '@client/lib/utils';
import { motion } from 'framer-motion';
import { AnswerOption } from './AnswerOption';
import type { QuestionConfig } from './questions';
import { TOTAL_QUESTIONS } from './questions';

interface QuestionViewProps {
  question: QuestionConfig;
  questionIndex: number;
  selectedValue: string | null;
  onSelectAnswer: (value: PolicyPreferenceValue | null) => void;
  onBack: () => void;
  onDone: () => void;
  onNext: () => void;
}

function ProgressDots({ currentIndex, total }: { currentIndex: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, index) => (
        <div
          key={index}
          className={cn(
            'h-2 w-2 rounded-full transition-colors',
            index <= currentIndex ? 'bg-primary' : 'bg-gray-200',
          )}
        />
      ))}
    </div>
  );
}

const variants = {
  enter: {
    y: 10,
    opacity: 0,
  },
  center: {
    y: 0,
    opacity: 1,
  },
  exit: {
    y: -10,
    opacity: 0,
  },
};

export function QuestionView({
  question,
  questionIndex,
  selectedValue,
  onSelectAnswer,
  onBack,
  onDone,
  onNext,
}: QuestionViewProps) {
  const isFirstQuestion = questionIndex === 0;
  const isLastQuestion = questionIndex === TOTAL_QUESTIONS - 1;

  return (
    <motion.div
      key={question.key}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-500">
            Question {questionIndex + 1} of {TOTAL_QUESTIONS}
          </span>
          <h3 className="text-lg font-semibold text-gray-900">{question.title}</h3>
        </div>
        <ProgressDots
          currentIndex={questionIndex}
          total={TOTAL_QUESTIONS}
        />
      </div>

      <p className="text-sm text-gray-600">{question.description}</p>

      <div
        className={cn(
          'grid gap-3',
          question.options.length === 2
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-1 sm:grid-cols-2',
        )}
      >
        {question.options.map(option => {
          const isSelected = selectedValue === option.value;
          return (
            <AnswerOption
              key={option.value}
              label={option.label}
              description={option.description}
              isSelected={isSelected}
              onClick={() =>
                onSelectAnswer(isSelected ? null : (option.value as PolicyPreferenceValue))
              }
            />
          );
        })}
      </div>

      <div className="mt-2 flex items-center justify-between">
        {!isFirstQuestion ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
          >
            Back
          </Button>
        ) : (
          <div />
        )}
        <div className="flex items-center justify-center gap-2">
          {!isLastQuestion && (
            <Button
              type="button"
              onClick={onNext}
              size="sm"
              variant="ghost"
              className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
              disabled={questionIndex >= TOTAL_QUESTIONS - 1}
            >
              Next
            </Button>
          )}
          <Button
            type="button"
            onClick={onDone}
            size="sm"
            variant="ghost"
            className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
          >
            Done
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
